// The branches a design is applied to from its Use dialog (GET/PUT
// /layouts/{id}/branches): what each branch shows now, what the person ticked,
// and what Apply would send.

import type { LayoutKind } from '../layout/types'

/** Only a ready branch may be ticked; the others say why not. */
export type BranchStatus = 'ready' | 'pending' | 'never' | 'profile' | 'none' | 'inactive'

export interface DesignBranch {
  branchId: string
  branchCode: string
  branchName: string
  status: BranchStatus
  /** Machines of the design's kind at the branch. */
  machines: number
  /** Of them, those that chose this design of their own. */
  onDesign: number
}

export type DesignOutcome =
  | 'applied'
  | 'unchanged'
  | Exclude<BranchStatus, 'ready'>
  | 'invalid'
  | 'would_strand'
  | 'busy'
  | 'failed'

export interface DesignResult {
  branchId: string
  branchCode: string
  branchName: string
  outcome: DesignOutcome
  detail?: string
  machines?: string[]
  version?: number
}

export type Tick = 'on' | 'some' | 'off'

/** The person's ticks that differ from what the server said, by branch id. */
export type Ticks = Readonly<Record<string, 'on' | 'off'>>

/** What the branch shows now: every machine on the design, some, or none. */
export function tickOf(b: DesignBranch): Tick {
  if (b.machines > 0 && b.onDesign >= b.machines) return 'on'
  return b.onDesign > 0 ? 'some' : 'off'
}

export function shownTick(b: DesignBranch, ticks: Ticks): Tick {
  return ticks[b.branchId] ?? tickOf(b)
}

/** One click on a ready branch: on ↔ off, and a mixed branch comes back to mixed. */
export function toggleTick(b: DesignBranch, ticks: Ticks): Ticks {
  if (b.status !== 'ready') return ticks
  const base = tickOf(b)
  const now = shownTick(b, ticks)
  let next: Tick = now === 'on' ? 'off' : 'on'
  if (base === 'some' && now === 'off') next = 'some'
  const out = { ...ticks }
  if (next === base || next === 'some') delete out[b.branchId]
  else out[b.branchId] = next
  return out
}

export interface BranchPlan {
  apply: string[]
  remove: string[]
  /** Machines that take the design, and the branches they are in. */
  adding: number
  addingBranches: number
  /** Machines that drop it, and their branches. */
  removing: number
  removingBranches: number
}

/** What Apply sends, and what it changes. Only ready branches count. */
export function planOf(lines: readonly DesignBranch[], ticks: Ticks): BranchPlan {
  const plan: BranchPlan = { apply: [], remove: [], adding: 0, addingBranches: 0, removing: 0, removingBranches: 0 }
  for (const b of lines) {
    const tick = ticks[b.branchId]
    if (!tick || b.status !== 'ready') continue
    if (tick === 'on') {
      plan.apply.push(b.branchId)
      plan.adding += b.machines - b.onDesign
      plan.addingBranches++
    } else {
      plan.remove.push(b.branchId)
      plan.removing += b.onDesign
      plan.removingBranches++
    }
  }
  return plan
}

export function hasPlan(p: BranchPlan): boolean {
  return p.apply.length + p.remove.length > 0
}

/** The machines a design of this kind is shown on. */
export type MachineNoun = 'kiosk' | 'display' | 'counter'

export function machineNoun(kind: LayoutKind): MachineNoun {
  if (kind === 'display') return 'display'
  if (kind === 'counter') return 'counter'
  return 'kiosk'
}

/** A result is good news when the branch now runs what was asked. */
export function succeeded(r: DesignResult): boolean {
  return r.outcome === 'applied' || r.outcome === 'unchanged'
}
